import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["certificate", "toast"]
  static values = {
    shareUrl: String,
    petName: String,
    filename: String
  }

  connect() {
    if (new URLSearchParams(window.location.search).get("print") === "1") {
      requestAnimationFrame(() => {
        this.print()
      })
    }
  }

  disconnect() {
    this.disableA4Layout()
  }

  async print() {
    await this.prepareExport()
    const cleanup = () => this.cleanupExport()
    window.addEventListener("afterprint", cleanup, { once: true })
    window.print()
  }

  async downloadPdf() {
    if (typeof html2pdf === "undefined") {
      this.notify("Não foi possível gerar o PDF. Tente imprimir e salvar como PDF.")
      await this.print()
      return
    }

    const toolbar = this.element.querySelector(".certificate-actions")
    if (toolbar) toolbar.style.visibility = "hidden"

    const sandbox = this.buildPdfSandbox()

    try {
      await this.waitForImages(sandbox)
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))

      const element = sandbox.querySelector(".adoption-certificate")
      const filename =
        this.filenameValue ||
        `certificado-adocao-${(this.petNameValue || "pet").replace(/\s+/g, "-").toLowerCase()}.pdf`

      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename,
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
          image: { type: "jpeg", quality: 0.96 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
            scrollX: 0,
            scrollY: -window.scrollY,
            backgroundColor: "#ffffff"
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
        })
        .from(element)
        .save()

      this.notify("PDF gerado em A4 com sucesso.")
    } catch (_error) {
      this.notify("Erro ao gerar PDF. Use Imprimir e escolha Salvar como PDF.")
    } finally {
      sandbox.remove()
      if (toolbar) toolbar.style.visibility = ""
    }
  }

  buildPdfSandbox() {
    const source = this.certificateTarget
    const sandbox = document.createElement("div")
    sandbox.className = "certificate-pdf-sandbox"
    sandbox.setAttribute("aria-hidden", "true")

    const clone = source.cloneNode(true)
    sandbox.appendChild(clone)
    document.body.appendChild(sandbox)

    // Garante URLs absolutas das imagens no clone (html2canvas)
    sandbox.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src")
      if (src && !src.startsWith("http") && !src.startsWith("data:") && !src.startsWith("blob:")) {
        img.src = new URL(src, window.location.origin).href
      }
    })

    return sandbox
  }

  async prepareExport() {
    this.enableA4Layout()
    window.scrollTo(0, 0)
    this.certificateTarget.scrollIntoView({ block: "start" })
    this.ensureImageSources(this.certificateTarget)
    await this.waitForImages(this.certificateTarget)
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  }

  ensureImageSources(root) {
    root.querySelectorAll("img.adoption-certificate__photo").forEach((img) => {
      const src = img.getAttribute("src")
      if (!src) return
      if (src.startsWith("data:")) return

      if (src.startsWith("/")) {
        img.src = new URL(src, window.location.origin).href
      }
    })
  }

  cleanupExport() {
    this.disableA4Layout()
  }

  waitForImages(root) {
    const images = Array.from(root.querySelectorAll("img"))
    return Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve()
              return
            }
            img.addEventListener("load", () => resolve(), { once: true })
            img.addEventListener("error", () => resolve(), { once: true })
            setTimeout(resolve, 4000)
          })
      )
    )
  }

  enableA4Layout() {
    document.body.classList.add("certificate-a4-export")
  }

  disableA4Layout() {
    document.body.classList.remove("certificate-a4-export")
  }

  async share() {
    const url = this.shareUrlValue
    const title = `Certificado de adoção — ${this.petNameValue || "CHC Petlar"}`

    try {
      if (navigator.share) {
        await navigator.share({ title, url })
        return
      }

      await navigator.clipboard.writeText(url)
      this.notify("Link copiado para a área de transferência.")
    } catch (error) {
      if (error?.name === "AbortError") return
      this.notify("Não foi possível compartilhar. Copie o link da barra de endereços.")
    }
  }

  notify(message) {
    if (!this.hasToastTarget) return

    this.toastTarget.textContent = message
    this.toastTarget.classList.add("certificate-toast--visible")
    clearTimeout(this.toastTimeout)
    this.toastTimeout = setTimeout(() => {
      this.toastTarget.classList.remove("certificate-toast--visible")
    }, 3200)
  }
}
